import {
  ACTIVATION_FEE_THRESHOLDS,
  CONSUMPTION_COLORS,
  CONSUMPTION_COLORS_DARK,
  CONSUMPTION_FEE_THRESHOLDS,
  DURATION_FEE_THRESHOLDS,
  LEVELS,
  TIME_COLORS,
  TIME_COLORS_DARK,
} from "@/constants";
import { formatCost, formatNumber, formatRate, formatTwoDigitTime, secondToHourMinuteText, timeBasedGetText, whToKwh } from "@/library/helpers";
import { i18n } from "@/library/third-party/i18n";
import { capitalize } from "lodash-es";
import moment from "moment-timezone";

const CONSUMPTION_STEP = 1000; //1 kwh step
const CONSUMPTION_MAX = 100000; // 100 kwh max

export function getPaymentPlanText({ type, values }) {
  const texts = {
    "consumption-less": ({ maxConsumption }) =>
      i18n.t("For_the_first_{amount}_kwh_of_electricity_consumed", {
        amount: !isNaN(parseFloat(maxConsumption)) ? whToKwh(maxConsumption) : "...",
      }),

    "consumption-between": ({ minConsumption, maxConsumption }) =>
      i18n.t("Between_{min}_kwh_and_{max}_kwh_of_electricity_consumed", {
        min: !isNaN(parseFloat(minConsumption)) ? whToKwh(minConsumption) : "...",
        max: !isNaN(parseFloat(maxConsumption)) ? whToKwh(maxConsumption) : "...",
      }),

    "consumption-more": ({ minConsumption }) =>
      i18n.t("For_more_than_{amount}_kwh_of_electricity_consumed", {
        amount: !isNaN(parseFloat(minConsumption)) ? whToKwh(minConsumption) : "...",
      }),

    "duration-less": ({ maxDuration }) =>
      i18n.t("For_the_first_{duration}_of_the_session", {
        duration: secondToHourMinuteText(maxDuration) || "...",
      }),

    "duration-between": ({ minDuration, maxDuration }) =>
      i18n.t("Between_session_duration_of_{min}_and_{max}", {
        min: secondToHourMinuteText(minDuration) || "...",
        max: secondToHourMinuteText(maxDuration) || "...",
      }),

    "duration-more": ({ minDuration }) =>
      i18n.t("For_session_duration_longer_than_{duration}", {
        duration: secondToHourMinuteText(minDuration) || "...",
      }),

    date: ({ minDay, minMonth, maxDay, maxMonth, maxHour = 0, maxMinute = 0 }) => {
      const from = (minDay || minMonth) && timeBasedGetText({ values: { day: minDay, month: minMonth, hour: 0, minute: 0 }, type: "date" });
      const to = (maxDay || maxMonth) && timeBasedGetText({ values: { day: maxDay, month: maxMonth, hour: maxHour, minute: maxMinute }, type: "date" });
      return i18n.t("Every_year_for_calendar_days_between_{from}_and_{to}_with_html", {
        from: from || "...",
        to: to || "...",
      });
    },

    day: ({ minWeekday, maxWeekday }) => {
      const from = !isNaN(parseInt(minWeekday)) && timeBasedGetText({ values: { weekday: minWeekday }, type: "day" });
      const to = !isNaN(parseInt(maxWeekday)) && timeBasedGetText({ values: { weekday: maxWeekday }, type: "day" });

      return i18n.t(minWeekday != maxWeekday || isNaN(parseInt(minWeekday)) || isNaN(parseInt(maxWeekday)) ? "Every_week_from_{from}_to_{to}" : "{day}_of_the_week", {
        from: from || "...",
        to: to || "...",
        day: from || "...",
      });
    },

    time: ({ maxHour, minHour, maxMinute, minMinute, maxAmPm, minAmPm }) => {
      const from =
        (!isNaN(parseInt(minHour)) || !isNaN(parseInt(minMinute)) || !!minAmPm) && timeBasedGetText({ values: { hour: minHour, minute: minMinute, amPm: minAmPm }, type: "time" });

      const to =
        (!isNaN(parseInt(maxHour)) || !isNaN(parseInt(maxMinute)) || !!maxAmPm) && timeBasedGetText({ values: { hour: maxHour, minute: maxMinute, amPm: maxAmPm }, type: "time" });

      return i18n.t("Every_day_for_time_of_day_between_{from}_and_{to}_with_html", {
        from: from || "...",
        to: to || "...",
      });
    },
  };

  return texts[type]({ ...values });
}

export function isPaymentPlanValueSame({ type, values }) {
  const isSame = {
    "consumption-less": ({}) => false,
    "consumption-between": ({ minConsumption, maxConsumption }) =>
      !isNaN(parseFloat(minConsumption)) &&
      !isNaN(parseFloat(maxConsumption)) &&
      (!isNaN(parseFloat(minConsumption)) ? parseFloat(minConsumption) : "...") === (!isNaN(parseFloat(maxConsumption)) ? parseFloat(maxConsumption) : "..."),
    "consumption-more": ({}) => false,
    "duration-less": ({}) => false,
    "duration-between": ({ minDuration, maxDuration }) =>
      secondToHourMinuteText(minDuration) &&
      secondToHourMinuteText(maxDuration) &&
      (secondToHourMinuteText(minDuration) || "...") === (secondToHourMinuteText(maxDuration) || "..."),
    "duration-more": ({}) => false,
    date: ({ minDay, minMonth, maxDay, maxMonth, maxHour = 0, maxMinute = 0 }) =>
      ((minDay || minMonth) && timeBasedGetText({ values: { day: minDay, month: minMonth, hour: 0, minute: 0 }, type: "date" })) ===
      ((maxDay || maxMonth) && timeBasedGetText({ values: { day: maxDay, month: maxMonth, hour: maxHour, minute: maxMinute }, type: "date" })),
    day: ({ minWeekday, maxWeekday }) => {
      return (
        (minWeekday && timeBasedGetText({ values: { weekday: minWeekday }, type: "day" })) === maxWeekday && timeBasedGetText({ values: { weekday: maxWeekday }, type: "day" })
      );
    },
    time: ({ maxHour, minHour, maxMinute, minMinute, maxAmPm, minAmPm }) =>
      ((!isNaN(parseInt(minHour)) || !isNaN(parseInt(minMinute)) || !!minAmPm) &&
        timeBasedGetText({ values: { hour: minHour, minute: minMinute, amPm: minAmPm }, type: "time" })) ===
        (!isNaN(parseInt(maxHour)) || !isNaN(parseInt(maxMinute)) || !!maxAmPm) && timeBasedGetText({ values: { hour: maxHour, minute: maxMinute, amPm: maxAmPm }, type: "time" }),
  };
  return isSame[type]({ ...values });
}

export function calculatePricing({ paymentPlan, start = null, end = null, currency }: any) {
  const now = start ? start : moment();
  let current = moment(now.format("YYYY-MM-DD HH:mm:ss"));
  let durationInSeconds = 0;
  let max = end ? end : moment().add(24, "hours");
  let minText = null;
  let maxText = null;
  let startTimestamp = null;
  let endTimestamp = null;
  let pricings = [];
  let lastText = null;
  let text = null;
  let lastPlans = null;
  while (current < max) {
    const { text: currentText, plans } = findText({ paymentPlan, momenttime: current, duration: durationInSeconds, currency, showActivationFee: pricings.length === 0 });
    text = currentText;
    if (!lastText) lastText = text;
    if (!lastPlans) lastPlans = plans;
    if (!minText) minText = current.format("YYYY-MM-DD HH:mm:ss") == moment().format("YYYY-MM-DD HH:mm:ss") ? "Now" : current.format("hh:mm A");
    if (!startTimestamp) startTimestamp = moment(current.format("YYYY-MM-DD HH:mm:ss"));
    if (lastText === text || maxText === null) maxText = current.format("hh:mm A");
    if (lastText === text || endTimestamp === null) endTimestamp = moment(current.format("YYYY-MM-DD HH:mm:ss"));
    if (lastText !== text) {
      maxText = current.format("hh:mm A");
      startTimestamp = moment(endTimestamp.format("YYYY-MM-DD HH:mm:ss"));
      endTimestamp = moment(current.format("YYYY-MM-DD HH:mm:ss"));
      pricings.push({ text: `${minText} - ${maxText}`, time: { start: startTimestamp, end: endTimestamp }, planText: lastText, plans: lastPlans });
      minText = maxText;
      const { text: currentText, plans } = findText({ paymentPlan, momenttime: current, duration: durationInSeconds, currency, showActivationFee: pricings.length === 0 });
      lastText = currentText;
      lastPlans = plans;
    }
    const next = 15 - (current.minute() % 15);
    current = current.add(next, "minutes");
    durationInSeconds += next * 60;
    if (current > max && lastText != null) {
      maxText = max.format("hh:mm A");
      endTimestamp = moment(max.format("YYYY-MM-DD HH:mm:ss"));
      pricings.push({ text: `${minText} - ${maxText}`, time: { start: startTimestamp, end: endTimestamp }, planText: lastText, plans: lastPlans });
    }
  }
  return pricings.length == 0 ? [{ text: `Now - ${max.format("hh:mm A")}`, time: { start: current, end: max }, planText: lastText, plans: lastPlans }] : pricings;
}

function parsePlanBlock(text: string) {
  const kwhMatch = text.match(/<strong>(\d+(?:\.\d{1,4})?(?:\s*-\s*\d+(?:\.\d{1,4})?)?|\d+(?:\.\d{1,4})?\+)\s*kWh<\/strong>/i);
  const kwhRange = kwhMatch ? kwhMatch[1].trim() + " kWh" : null;

  const activationMatch = text.match(/Activation Fee.*?<strong>(.*?)<\/strong>/i);
  const hasActivationFee = !!activationMatch;
  const startSessionCost = hasActivationFee ? formatNumber(activationMatch[1]) : null;

  const durationMatch = text.match(/Duration Rate.*?<strong>(.*?)<\/strong>/i);
  const consumptionMatch = text.match(/Consumption Rate.*?<strong>(.*?)\s*\/\s*kWh<\/strong>/i);

  const duration = formatNumber(durationMatch?.[1]);
  const consumption = formatNumber(consumptionMatch?.[1]);

  const disabled = text.toLowerCase().includes("device is disabled");

  return {
    kwhRange,
    startSessionCost,
    duration,
    consumption,
    whRate: consumption,
    disabled,
    showActivation: hasActivationFee,
  };
}

function sortPlans(plans: any[]) {
  const parseKwh = (s: string | null) => {
    if (!s) return 0;
    if (s.includes("+")) return Number.MAX_SAFE_INTEGER;
    const m = s.match(/\d+(\.\d+)?/);
    return m ? parseFloat(m[0]) : 0;
  };
  return plans.sort((a, b) => parseKwh(a.kwhRange) - parseKwh(b.kwhRange));
}

export function getRateLevel(rateValue: number, thresholds: Record<string, number>): number {
  if (rateValue === undefined || typeof rateValue !== "number" || isNaN(rateValue) || rateValue <= 0) return LEVELS.NONE;

  if (rateValue > thresholds.HIGH) return LEVELS.HIGH;
  if (rateValue >= thresholds.MEDIUM) return LEVELS.MEDIUM;
  if (rateValue >= thresholds.LOW) return LEVELS.LOW;

  return LEVELS.NONE;
}

export function getPricingLevel(activationRate: number | null | undefined, durationRate: number | null | undefined, consumptionRate: number | null | undefined): number {
  const activationRateValue = activationRate === null || activationRate === undefined || isNaN(activationRate) ? 0 : activationRate;
  const durationRateValue = durationRate === null || durationRate === undefined || isNaN(durationRate) ? 0 : durationRate;
  const consumptionRateValue = consumptionRate === null || consumptionRate === undefined || isNaN(consumptionRate) ? 0 : consumptionRate;

  const activationLevel = getRateLevel(activationRateValue, ACTIVATION_FEE_THRESHOLDS);
  const durationLevel = getRateLevel(durationRateValue, DURATION_FEE_THRESHOLDS);
  const consumptionLevel = getRateLevel(consumptionRateValue, CONSUMPTION_FEE_THRESHOLDS);

  return Math.max(activationLevel, durationLevel, consumptionLevel);
}

function calculatePlanCost(plan: any) {
  const start = parseFloat(plan.startSessionCost) || 0;
  const duration = parseFloat(plan.duration) || 0;
  const consumption = parseFloat(plan.consumption) || 0;
  return start + duration + consumption;
}

export function getPricingBorderColor(plans: any[] = [], type: "time" | "consumption" = "time", darkTheme: boolean) {
  if (!plans.length) return { outerColor: "#BDBDBD", innerColor: null };

  const shades = type === "consumption" ? (darkTheme ? CONSUMPTION_COLORS_DARK : CONSUMPTION_COLORS) : darkTheme ? TIME_COLORS_DARK : TIME_COLORS;

  let priorityLevel = LEVELS.NONE;

  for (const plan of plans) {
    if (plan.disabled) continue;

    const currentPlanLevel = getPricingLevel(parseFloat(plan.startSessionCost) || 0, parseFloat(plan.duration) || 0, parseFloat(plan.consumption) || 0);
    priorityLevel = Math.max(priorityLevel, currentPlanLevel);
  }

  const borderColor = shades[priorityLevel];

  return borderColor;
}

/**
 * 1. **Parsing & Structuring:**
 * - Converts the raw `planText` string into a structured and sortable array of plan objects.
 *
 * 2. **Computation:**
 * - Calculates individual plan totals (`totalSum`).
 * - Aggregates all plan totals into a single `totalSum` for the parent pricing item.
 *
 * @param {Object} params - The configuration object.
 * @param {any[]} params.pricingData - The list of pricing items to process.
 * @returns {any[]} An array of processed pricing items, each including:
 * - `plans`: Parsed and sorted plan objects, each with a `totalSum`.
 * - `totalSum`: The computed cumulative cost (formatted to two decimals).
 */
export function calculateConditionalPricing({ pricingData }: { pricingData: any[] }) {
  return pricingData.map((item) => {
    const planText = item.planText || "";
    const planBlocks = planText.split(/<\/div>\s*<strong>/i);

    const parsedPlans = planBlocks.map((block, idx) => {
      const text = idx === 0 ? block : "<strong>" + block;
      return parsePlanBlock(text);
    });

    const allPlans = sortPlans([...parsedPlans]);

    const plansWithTotalCost = allPlans.map((plan) => {
      const planTotalCost = calculatePlanCost(plan);
      return {
        ...plan,
        totalSum: planTotalCost.toFixed(2),
      };
    });

    const pricingItemTotal = plansWithTotalCost.reduce((sum, plan) => {
      return sum + parseFloat(plan.totalSum);
    }, 0);

    return {
      ...item,
      plans: plansWithTotalCost,
      totalSum: pricingItemTotal.toFixed(2),
    };
  });
}

export function getConditionalPlanType(paymentPlan: any): "consumption" | "time" {
  const unlessPlans = paymentPlan?.unless?.map((u: any) => u.plan || {}) || [];
  const allPlans = [{ whRate: paymentPlan.whRate, startSessionCost: paymentPlan.startSessionCost, hRate: paymentPlan.hRate }, ...unlessPlans];

  for (const plan of allPlans)
    if (plan.hRate && plan.hRate !== 0) return "time";
    else if (plan.whRate && plan.whRate !== 0) return "consumption";

  return "time";
}

function timeMatch({ condition, current, duration = 0, timezone, consumption }) {
  const currentYear = current.year();
  const currentWeekday = current.day();
  const type = getConditionalType(condition, false);
  if (type === "date") {
    const minDate = moment(`${currentYear}-${condition.minMonth.toString().padStart(2, "0")}-${condition.minDay.toString().padStart(2, "0")} 00:00:00`).tz(timezone);
    const maxDate = moment(`${currentYear}-${condition.maxMonth.toString().padStart(2, "0")}-${condition.maxDay.toString().padStart(2, "0")} 23:59:59`).tz(timezone);
    if (minDate > maxDate) return !(maxDate < current && current <= minDate);
    else return minDate < current && current <= maxDate;
  } else if (type === "day") {
    const maxWeekday = condition.maxWeekday;
    const minWeekday = condition.minWeekday;
    const currentTime = current.format("HH:mm:ss");
    if (minWeekday == maxWeekday) return true;
    if (currentTime == "00:00:00" && maxWeekday == currentWeekday) return true;
    if (minWeekday < maxWeekday) return minWeekday <= currentWeekday && currentWeekday <= maxWeekday;
    if (minWeekday > maxWeekday) return minWeekday <= currentWeekday || currentWeekday < maxWeekday;
  } else if (type == "time") {
    const minTime = formatTwoDigitTime(condition.minHour) + ":" + formatTwoDigitTime(condition.minMinute) + ":00";
    const maxTime = formatTwoDigitTime(condition.maxHour) + ":" + formatTwoDigitTime(condition.maxMinute) + ":00";

    const currentTime = current.format("HH:mm:ss");
    if (minTime > maxTime) return !(maxTime < currentTime && currentTime <= minTime);
    else return minTime < currentTime && currentTime <= maxTime;
  } else if (type === "duration" && duration >= 0) {
    const { minDuration, maxDuration } = condition;
    if (minDuration & maxDuration) return minDuration <= duration && duration < maxDuration;
    if (minDuration) return minDuration <= duration;
    if (maxDuration) return duration < maxDuration;
  } else if (type === "consumption" && consumption >= 0) {
    const { maxConsumption, minConsumption } = condition;
    if (minConsumption && maxConsumption) return minConsumption <= consumption && consumption < maxConsumption;
    if (minConsumption) return minConsumption <= consumption;
    if (maxConsumption) return consumption < maxConsumption;
  }
  return false;
}

export function findPlan({ plan = null, momenttime = null, duration = 0, consumption, tz = "UTC" }) {
  let current = momenttime ? moment(momenttime.format("YYYY-MM-DD HH:mm:ss")) : moment();
  const timezone = plan.timezone || tz;
  current = current.tz(timezone);

  if (plan.unless) {
    for (let i = 0; i < plan.unless.length; i++) {
      const condition = plan.unless[i].condition;
      if (timeMatch({ condition, current, duration, timezone, consumption })) {
        return findPlan({ plan: plan.unless[i].plan, momenttime, duration, consumption, tz: timezone });
      }
    }
  }
  return plan;
}

export function findText({ paymentPlan = null, momenttime = null, duration = 0, currency, showActivationFee = false }) {
  let current = momenttime ? moment(momenttime.format("YYYY-MM-DD HH:mm:ss")) : moment();
  const timezone = paymentPlan?.timezone || "UTC";
  current = current.tz(timezone);

  let currentConsumption = 0;
  let lastConsumption = 0;
  let validPlans = [];
  let lastPlanText = null;
  let lastPlanFullText = null;
  let lastPlan = null;
  while (currentConsumption < CONSUMPTION_MAX) {
    const plan = findPlan({ plan: paymentPlan, momenttime, duration, consumption: currentConsumption, tz: timezone });
    const { full: fullText, withoutActivationFeeText } = planToText(plan, currency);
    if (!lastPlanFullText) lastPlanFullText = fullText;
    if (!lastPlanText) lastPlanText = withoutActivationFeeText;
    if (!lastPlan) lastPlan = plan;
    if (lastPlanText !== withoutActivationFeeText) {
      validPlans.push({
        consumptionText: `<strong>${whToKwh(lastConsumption)} - ${whToKwh(currentConsumption)} kWh</strong>`,
        planText: showActivationFee ? lastPlanFullText : lastPlanText,
        plan: lastPlan,
      });
      showActivationFee = false;
      lastConsumption = currentConsumption;
      lastPlanFullText = fullText;
      lastPlanText = withoutActivationFeeText;
    }
    currentConsumption += CONSUMPTION_STEP;
    if (currentConsumption >= CONSUMPTION_MAX) {
      validPlans.push({
        consumptionText: `<strong>${whToKwh(lastConsumption)}+ kWh</strong>`,
        planText: showActivationFee ? lastPlanFullText : lastPlanText,
        plan,
      });
    }
  }
  if (validPlans.length === 1)
    return {
      text: validPlans[0].planText,
      plans: [validPlans[0].plan],
    };

  return {
    text: validPlans.map((t) => `${t.consumptionText}:<br><div style="padding-left: 12px;">${t.planText}</div>`).join(""),
    plans: validPlans.map((p) => p.plan),
  };

  function planToText(plan, currency) {
    const disabledText = `<span class="error--text">Device is disabled during this time period. Sessions will automatically terminate. Please contact management for more information.</span>`;
    if (plan.disabled)
      return {
        full: disabledText,
        activationFeeText: disabledText,
        withoutActivationFeeText: disabledText,
      };
    const activationFeeText = `Activation Fee (${currency}): <strong>${formatCost(plan.startSessionCost || 0)}</strong><br>`;
    const withoutActivationFeeText = `Duration Rate (${currency}): <strong>${formatRate(plan.hRate || 0, "h")}</strong><br>Consumption Rate (${currency}): <strong>${formatRate(
      ((plan.whRate || 0) * 1000000000) / 1000000,
      "kWh"
    )}</strong>`;
    return {
      full: `${activationFeeText}${withoutActivationFeeText}`,
      activationFeeText,
      withoutActivationFeeText,
    };
  }
}

export function isPricingFree(pricings = [], only_activation = false) {
  let isFree = true;
  for (let i = 0; i < pricings.length; i++) {
    const price = pricings[i];
    if (!price.plans) continue;
    for (let j = 0; j < price.plans.length; j++) {
      let { startSessionCost, hRate, whRate } = price.plans[j];
      startSessionCost = startSessionCost || 0;
      hRate = hRate || 0;
      whRate = whRate || 0;
      if (only_activation) {
        if (startSessionCost > 0 || hRate > 0 || whRate > 0) isFree = false;
        break;
      }
      if (startSessionCost > 0 || hRate > 0 || whRate > 0) {
        isFree = false;
        break;
      }
    }
    if (!isFree || only_activation) break;
  }
  return isFree;
}

export function getConditionalType(condition, detailed = true) {
  const conditions = {
    date: ["minMonth", "maxMonth", "minDay", "maxDay"],
    day: ["minWeekday", "maxWeekday"],
    time: ["minHour", "maxHour", "minMinute", "maxMinute"],
    duration: ["minDuration", "maxDuration"],
    consumption: ["minConsumption", "maxConsumption"],
  };
  let selectedKey = null;
  for (const type in conditions) {
    const properties = conditions[type];
    for (const k in condition) {
      if (properties.includes(k)) {
        selectedKey = type;
        break;
      }
    }
    if (selectedKey) break;
  }
  if (detailed && ["duration", "consumption"].includes(selectedKey)) {
    if (!condition[`min${capitalize(selectedKey)}`]) selectedKey = `${selectedKey}-less`;
    else if (!condition[`max${capitalize(selectedKey)}`]) selectedKey = `${selectedKey}-more`;
    else selectedKey = `${selectedKey}-between`;
  }
  return selectedKey;
}
